(function () {
  'use strict';

  const elementName = 'aa-header-search';

  if (customElements.get(elementName)) return;

  const recentSearchKey = 'aa_header_recent_search_v1';
  const viewedProductsKey = 'viewedProducts';

  class AAHeaderSearch extends HTMLElement {
    constructor() {
      super();

      this.openSearch = this.openSearch.bind(this);
      this.closeSearch = this.closeSearch.bind(this);
      this.handleClose = this.handleClose.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleTrendingClick = this.handleTrendingClick.bind(this);
      this.handleFooterLinkClick =
        this.handleFooterLinkClick.bind(this);
    }

    connectedCallback() {
      if (this.dataset.bound === 'true') return;

      this.dataset.bound = 'true';

      this.header = this.closest('archetype-custom-header');
      this.dialog = this.querySelector('[data-aa-search-dialog]');
      this.form = this.querySelector('[data-aa-search-form]');
      this.input = this.querySelector('[data-aa-search-input]');
      this.closeButton = this.querySelector('[data-aa-search-close]');
      this.products = this.querySelector('[data-aa-search-products]');
      this.status = this.querySelector('[data-aa-search-status]');
      this.resultsTitle = this.querySelector(
        '[data-aa-search-results-title]'
      );
      this.topViewAll = this.querySelector(
        '[data-aa-search-view-all]'
      );
      this.footer = this.querySelector('[data-aa-search-footer]');
      this.footerLabel = this.querySelector(
        '[data-aa-search-footer-label]'
      );
      this.footerLink = this.querySelector(
        '[data-aa-search-footer-link]'
      );

      this.openButtons = this.header
        ? this.header.querySelectorAll('[data-aa-search-open]')
        : [];

      if (
        !this.dialog ||
        !this.form ||
        !this.input ||
        !this.products
      ) {
        return;
      }

      this.fallbackMarkup = this.products.innerHTML;
      this.fallbackTitle = this.resultsTitle
        ? this.resultsTitle.textContent.trim()
        : 'BEST SELLERS';
      this.fallbackViewAllUrl = this.topViewAll
        ? this.topViewAll.href
        : '';

      this.currentMode = 'fallback';
      this.currentSearch = null;
      this.inputTimer = 0;
      this.requestController = null;

      this.openButtons.forEach((button) => {
        button.addEventListener('click', this.openSearch);
      });

      this.closeButton?.addEventListener(
        'click',
        this.closeSearch
      );

      this.dialog.addEventListener('close', this.handleClose);
      this.input.addEventListener('input', this.handleInput);
      this.form.addEventListener('submit', this.handleSubmit);

      this.querySelectorAll('[data-aa-trending-search]').forEach(
        (link) => {
          link.addEventListener(
            'click',
            this.handleTrendingClick
          );
        }
      );

      this.footerLink?.addEventListener(
        'click',
        this.handleFooterLinkClick
      );

      this.syncWishlistButtons(this.products);
    }

    disconnectedCallback() {
      this.openButtons.forEach((button) => {
        button.removeEventListener('click', this.openSearch);
      });

      this.closeButton?.removeEventListener(
        'click',
        this.closeSearch
      );

      this.dialog?.removeEventListener(
        'close',
        this.handleClose
      );

      this.input?.removeEventListener('input', this.handleInput);
      this.form?.removeEventListener('submit', this.handleSubmit);

      this.querySelectorAll('[data-aa-trending-search]').forEach(
        (link) => {
          link.removeEventListener(
            'click',
            this.handleTrendingClick
          );
        }
      );

      this.footerLink?.removeEventListener(
        'click',
        this.handleFooterLinkClick
      );

      window.clearTimeout(this.inputTimer);
      this.abortRequest();
    }

    openSearch(event) {
      event?.preventDefault();

      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) {
          this.dialog.showModal();
        }
      } else {
        this.dialog.setAttribute('open', '');
      }

      document.documentElement.classList.add('aa-search-open');

      this.input.value = '';
      this.showInitialState();

      window.requestAnimationFrame(() => {
        this.input.focus({
          preventScroll: true
        });
      });
    }

    closeSearch(event) {
      event?.preventDefault();

      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.handleClose();
      }
    }

    handleClose() {
      document.documentElement.classList.remove('aa-search-open');

      window.clearTimeout(this.inputTimer);
      this.abortRequest();

      this.input.value = '';
      this.setLoading(false);
    }

    handleInput() {
      const query = this.input.value.trim();

      window.clearTimeout(this.inputTimer);

      if (!query) {
        this.showInitialState();
        return;
      }

      this.inputTimer = window.setTimeout(() => {
        this.loadProducts({
          query: query,
          label: query,
          mode: 'live'
        });
      }, 250);
    }

    handleSubmit(event) {
      const query = this.input.value.trim();

      if (!query) {
        event.preventDefault();
        return;
      }

      this.saveRecentSearch(query, query);
    }

    handleTrendingClick(event) {
      const link = event.currentTarget;
      const query = link.getAttribute('data-aa-query') || '';
      const label =
        link.getAttribute('data-aa-label') || query;

      if (!query) return;

      this.saveRecentSearch(query, label);
    }

    handleFooterLinkClick() {
      if (
        this.currentSearch &&
        this.currentSearch.query &&
        this.currentMode !== 'viewed'
      ) {
        this.saveRecentSearch(
          this.currentSearch.query,
          this.currentSearch.label
        );
      }
    }

    showInitialState() {
      const recentSearch = this.getRecentSearch();

      if (recentSearch && recentSearch.query) {
        this.loadProducts({
          query: recentSearch.query,
          label: recentSearch.label || recentSearch.query,
          mode: 'recent'
        });

        return;
      }

      const viewedProducts = this.getViewedProducts();

      if (viewedProducts.length) {
        const idQuery = viewedProducts
          .slice(0, 4)
          .map((id) => `id:${id}`)
          .join(' OR ');

        this.loadProducts({
          query: idQuery,
          label: '',
          mode: 'viewed'
        });

        return;
      }

      this.showFallback();
    }

    showFallback() {
      this.abortRequest();

      this.currentMode = 'fallback';
      this.currentSearch = null;

      this.products.innerHTML = this.fallbackMarkup;

      if (this.resultsTitle) {
        this.resultsTitle.textContent = this.fallbackTitle;
      }

      if (this.topViewAll) {
        this.topViewAll.hidden = !this.fallbackViewAllUrl;
        this.topViewAll.href = this.fallbackViewAllUrl;
      }

      if (this.footer) {
        this.footer.hidden = true;
      }

      this.setLoading(false);
      this.syncWishlistButtons(this.products);
    }

    async loadProducts({ query, label, mode }) {
      if (!query) {
        this.showFallback();
        return;
      }

      this.abortRequest();

      this.requestController = new AbortController();

      const requestSignal = this.requestController.signal;
const isViewedMode = mode === 'viewed';

const sectionUrl = new URL(
  isViewedMode
    ? (
        this.dataset.searchUrl ||
        '/search'
      )
    : (
        this.dataset.predictiveSearchUrl ||
        '/search/suggest'
      ),
  window.location.origin
);

sectionUrl.searchParams.set(
  'q',
  query
);

sectionUrl.searchParams.set(
  'section_id',
  this.dataset.sectionId ||
    'custom-header-search-products'
);

if (isViewedMode) {
  /*
   * Recently viewed products use the
   * existing id:123 OR id:456 query.
   */
  sectionUrl.searchParams.set(
    'type',
    'product'
  );
} else {
  /*
   * Normal typing / recent searches
   * use Shopify Predictive Search,
   * matching Horizon's search engine.
   */
  sectionUrl.searchParams.set(
    'resources[type]',
    'product'
  );

  sectionUrl.searchParams.set(
    'resources[limit]',
    '4'
  );

  sectionUrl.searchParams.set(
    'resources[limit_scope]',
    'each'
  );
}

      this.setLoading(true);

      try {
        const response = await fetch(sectionUrl.toString(), {
          credentials: 'same-origin',
          signal: requestSignal,
          headers: {
            Accept: 'text/html'
          }
        });

        if (!response.ok) {
          throw new Error('Unable to load search products.');
        }

        const markup = await response.text();

        if (requestSignal.aborted) return;

        const parsedDocument = new DOMParser().parseFromString(
          markup,
          'text/html'
        );

        const nextResults = parsedDocument.querySelector(
          '.aa-search-section-results'
        );

        if (!nextResults) {
          throw new Error(
            'Search product section markup was not found.'
          );
        }

        this.products.innerHTML = nextResults.innerHTML;

if (
  window.customElements &&
  typeof window.customElements.upgrade === 'function'
) {
  window.customElements.upgrade(this.products);
}

this.currentMode = mode;
        this.currentSearch = {
          query: query,
          label: label || query
        };

        this.configureMode(mode, label || query);
        this.syncWishlistButtons(this.products);
      } catch (error) {
        if (
          error.name === 'AbortError' ||
          requestSignal.aborted
        ) {
          return;
        }

        console.error(
          '[custom-header-search] Product request failed.',
          error
        );

        this.products.innerHTML =
          `<p class="aa-search__no-results">${
            this.dataset.errorText ||
            'Unable to load products.'
          }</p>`;

        this.configureMode(mode, label || query);
      } finally {
        if (!requestSignal.aborted) {
          this.setLoading(false);
        }
      }
    }

    configureMode(mode, label) {
      if (this.topViewAll) {
        this.topViewAll.hidden = true;
      }

      if (mode === 'viewed') {
        if (this.resultsTitle) {
          this.resultsTitle.textContent =
            this.dataset.recentHeading ||
            'RECENTLY VIEWED';
        }

        if (this.footer) {
          this.footer.hidden = true;
        }

        return;
      }

      const displayLabel = label || '';

      if (mode === 'recent') {
        const headingPrefix =
          this.dataset.keepShoppingPrefix ||
          'KEEP SHOPPING FOR';

        if (this.resultsTitle) {
          this.resultsTitle.textContent =
            `${headingPrefix} ${displayLabel}`.trim();
        }

        if (this.footerLabel) {
          this.footerLabel.textContent =
            this.dataset.recentSearchLabel ||
            'Recent search';
        }
      } else {
        const resultsPrefix =
          this.dataset.resultsPrefix ||
          'SEARCH RESULTS FOR';

        if (this.resultsTitle) {
          this.resultsTitle.textContent =
            `${resultsPrefix} "${displayLabel}"`;
        }

        if (this.footerLabel) {
          this.footerLabel.textContent =
            this.dataset.resultsFooterLabel ||
            'Search results';
        }
      }

      if (this.footer && this.footerLink) {
        const query =
          this.currentSearch && this.currentSearch.query
            ? this.currentSearch.query
            : displayLabel;

        this.footer.hidden = false;
        this.footerLink.href = this.createSearchUrl(query);
        this.footerLink.textContent =
          `${
            this.dataset.viewAllLabel ||
            'View all'
          } "${displayLabel}"`;
      }
    }

createSearchUrl(query) {
  const url = new URL(
    this.dataset.searchUrl || '/search',
    window.location.origin
  );

  url.searchParams.set(
    'q',
    query
  );

  url.searchParams.set(
    'type',
    'product'
  );

  url.searchParams.set(
    'options[prefix]',
    'last'
  );

  return url.toString();
}

    setLoading(loading) {
      if (this.status) {
        this.status.hidden = !loading;
      }

      if (this.products) {
        this.products.classList.toggle(
          'is-loading',
          Boolean(loading)
        );

        this.products.setAttribute(
          'aria-busy',
          loading ? 'true' : 'false'
        );
      }
    }

    abortRequest() {
      if (this.requestController) {
        this.requestController.abort();
        this.requestController = null;
      }
    }

    saveRecentSearch(query, label) {
      try {
        window.localStorage.setItem(
          recentSearchKey,
          JSON.stringify({
            query: String(query || '').trim(),
            label: String(label || query || '').trim()
          })
        );
      } catch (error) {
        console.warn(
          '[custom-header-search] Could not save recent search.',
          error
        );
      }
    }

    getRecentSearch() {
      try {
        const storedValue =
          window.localStorage.getItem(recentSearchKey);

        if (!storedValue) return null;

        const parsedValue = JSON.parse(storedValue);

        if (typeof parsedValue === 'string') {
          return {
            query: parsedValue,
            label: parsedValue
          };
        }

        if (
          parsedValue &&
          typeof parsedValue === 'object' &&
          parsedValue.query
        ) {
          return {
            query: String(parsedValue.query),
            label: String(
              parsedValue.label || parsedValue.query
            )
          };
        }
      } catch (error) {
        console.warn(
          '[custom-header-search] Could not read recent search.',
          error
        );
      }

      return null;
    }

    getViewedProducts() {
      try {
        const viewedProducts = JSON.parse(
          window.localStorage.getItem(viewedProductsKey) ||
            '[]'
        );

        if (!Array.isArray(viewedProducts)) return [];

        return viewedProducts
          .map((id) => String(id || '').trim())
          .filter(Boolean)
          .slice(0, 4);
      } catch (error) {
        console.warn(
          '[custom-header-search] Could not read viewed products.',
          error
        );

        return [];
      }
    }

    getWishlistHandles() {
      const handles = new Set();
      const config = window.CartWishlistConfig || {};

      const possibleKeys = [
        'archetype_wishlist_variants_v1',
        'archetype_wishlist_variants_v1_guest'
      ];

      if (config.customerId) {
        possibleKeys.push(
          `archetype_wishlist_variants_v1_${config.customerId}`
        );
      }

      possibleKeys.forEach((key) => {
        try {
          const items = JSON.parse(
            window.localStorage.getItem(key) || '[]'
          );

          if (!Array.isArray(items)) return;

          items.forEach((item) => {
            if (item && item.handle) {
              handles.add(String(item.handle));
            }
          });
        } catch (error) {
          // Ignore malformed legacy wishlist storage.
        }
      });

      return handles;
    }

    syncWishlistButtons(container) {
      if (!container) return;

      const savedHandles = this.getWishlistHandles();

      container
        .querySelectorAll(
          '.wishlist-toggle[data-product-handle]'
        )
        .forEach((button) => {
          const handle = button.getAttribute(
            'data-product-handle'
          );

          const active = savedHandles.has(handle);

          button.classList.toggle('is-active', active);
          button.setAttribute(
            'aria-pressed',
            active ? 'true' : 'false'
          );
        });
    }
  }

  customElements.define(elementName, AAHeaderSearch);
})();